<script lang="ts">
	import { PortableText } from "@portabletext/svelte";
	import { contentBlockComponents, Button } from "@ishavskraft/shared";
	import type { PortableTextBlock } from "@portabletext/types";

	let {
		summary,
	}: {
		summary?: { content?: PortableTextBlock[] } | null;
	} = $props();
	const uid = $props.id();
	let expanded = $state(false);
	let blocks = $derived(summary?.content ?? []);
	// An editor may paste an intro paragraph before the bullets, or no bullets at all.
	let head = $derived.by(() => {
		const firstItem = blocks.findIndex((block) => !!(block as { listItem?: string })?.listItem);
		return blocks.slice(0, (firstItem < 0 ? 0 : firstItem) + 1);
	});
	let rest = $derived(blocks.slice(head.length));
	// Only text teases well — a clipped image is just a sliver.
	let peek = $derived(rest[0]?._type === "block" ? [rest[0]] : []);
</script>

{#if blocks.length}
	<!-- Split so edge spacing and box padding don't both land on `padding-inline`. -->
	<div class="px-edge-x max-w-prose-edge-x mx-auto my-6">
		<section aria-labelledby="{uid}-heading" class="bg-isbre rounded-2xl p-4 text-sm">
			<h2 id="{uid}-heading" class="heading font-bold mb-2">Oppsummering</h2>

			<div id="{uid}-content" class="content *:mt-0">
				{#if expanded}
					<!-- One pass, so the bullets stay a single list -->
					<PortableText
						value={blocks}
						components={contentBlockComponents}
						onMissingComponent={false}
					/>
				{:else}
					<PortableText
						value={head}
						components={contentBlockComponents}
						onMissingComponent={false}
					/>
					{#if peek.length}
						<div class="peek" inert aria-hidden="true">
							<PortableText
								value={peek}
								components={contentBlockComponents}
								onMissingComponent={false}
							/>
						</div>
					{/if}
				{/if}
			</div>

			{#if rest.length}
				<Button
					aria-expanded={expanded}
					aria-controls="{uid}-content"
					onclick={() => (expanded = !expanded)}
					secondary
				>
					<!-- Visible text leads the accessible name, so voice control can target it -->
					{expanded ? "Vis mindre" : "Vis mer"}<span class="sr-only"> av oppsummeringen</span>
				</Button>
			{/if}
		</section>
	</div>
{/if}

<style lang="postcss">
	/* PostPage styles every h2 inside `article` from an unlayered rule, which
	   outranks utilities. Two classes out-specify it. */
	.heading.heading {
		font-size: var(--font-size-body);
		margin-block: 0 var(--s-2);
	}

	/* Collapsed, head and teaser are two lists — drop the gap the list margin
	   adds, so both states space the bullets alike. */
	.content :global(:has(+ .peek)) {
		margin-block-end: 0;
	}

	/* Clipped rather than cut at the first sentence: "f.eks." defeats any split. */
	.peek {
		max-height: 1lh;
		overflow: clip;
		mask-image: linear-gradient(to bottom, black, transparent);
	}

	/* Margins would push the visible line out of the clip box. */
	.peek :global(> *) {
		margin-block: 0;
	}
</style>
